import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { PortfolioToggleService } from "@/services/portfolio-toggle.service";

export function usePortfolioToggle(portfolioType) {
	if (!PortfolioToggleService.validatePortfolioType(portfolioType)) {
		throw new Error(`Invalid portfolio type: ${portfolioType}`);
	}

	const store = useStore();
	const router = useRouter();
	const route = useRoute();

	const active = ref(false);
	const portfolioConfig =
		PortfolioToggleService.getPortfolioConfig(portfolioType);

	const activePortfolio = computed(
		() => store.state.portfolios.activePortfolio,
	);
	const activeLayer = computed(() => store.getters.activeLayer);
	const getTranslations = computed(
		() => store.getters["staticContent/getTranslations"],
	);

	const toggleId = computed(() => `${portfolioConfig.slug}-toggle`);
	const ariaLabel = computed(() => `Toggle ${portfolioConfig.name}`);
	const buttonAriaLabel = computed(
		() => `Open ${portfolioConfig.name.toLowerCase()}`,
	);

	const isProcessing = ref(false);

	const setActivePortfolio = (portfolio) => {
		store.dispatch("portfolios/setActivePortfolio", {
			activePortfolio: portfolio,
		});
	};

	const setLayerAndRedirect = (layerId) => {
		const routeParams = PortfolioToggleService.createRouteParams(
			route,
			layerId,
		);
		router.push(routeParams);
	};

	const deactivateOtherToggles = () => {
		const event = new CustomEvent("portfolio-toggle-activated", {
			detail: { activeToggle: portfolioType },
		});
		document.dispatchEvent(event);
	};

	const handleToggleChange = () => {
		if (isProcessing.value) return;
		isProcessing.value = true;
		
		try {
			toggleActive();
		} finally {
			setTimeout(() => {
				isProcessing.value = false;
			}, 300);
		}
	};

	const openPortfolio = () => {
		if (isProcessing.value) return;
		isProcessing.value = true;

		try {
			setLayerAndRedirect(PortfolioToggleService.getSolutionsLayerId());
			const targetPortfolio = portfolioConfig;

			if (active.value) {
				const defaultPortfolio = PortfolioToggleService.getDefaultPortfolio();
				setActivePortfolio(defaultPortfolio);
				active.value = false;
			} else {
				deactivateOtherToggles();
				setActivePortfolio(targetPortfolio);
				active.value = true;
			}
		} finally {
			setTimeout(() => {
				isProcessing.value = false;
			}, 300);
		}
	};

	const toggleActive = () => {
		if (isProcessing.value) return;

		try {
			if (active.value) {
				const defaultPortfolio = PortfolioToggleService.getDefaultPortfolio();
				setActivePortfolio(defaultPortfolio);
				active.value = false;
			} else {
				deactivateOtherToggles();
				setActivePortfolio(portfolioConfig);
				active.value = true;
			}
		} catch (error) {
			console.error('Error toggling portfolio:', error);
			active.value = false;
		}
	};

	const deactivateToggle = () => {
		if (active.value) {
			const defaultPortfolio = PortfolioToggleService.getDefaultPortfolio();
			setActivePortfolio(defaultPortfolio);
			active.value = false;
		}
	};

	const handleToggleActivation = (event) => {
		if (event.detail.activeToggle !== portfolioType && active.value) {
			active.value = false;
		}
	};

	watch(
		activePortfolio,
		(newPortfolio) => {
			const shouldBeActive = PortfolioToggleService.isPortfolioActive(
				newPortfolio,
				portfolioType,
			);

			if (active.value !== shouldBeActive) {
				active.value = shouldBeActive;
			}
		},
		{ immediate: true },
	);

	watch(
		activeLayer,
		(newLayer, oldLayer) => {
			const solutionsLayerId = PortfolioToggleService.getSolutionsLayerId();

			if (
				oldLayer?.id === solutionsLayerId &&
				newLayer?.id !== solutionsLayerId
			) {
				deactivateToggle();
			}
		},
		{ immediate: false },
	);

	onMounted(() => {
		document.addEventListener("portfolio-toggle-activated", handleToggleActivation);
	});

	onUnmounted(() => {
		document.removeEventListener("portfolio-toggle-activated", handleToggleActivation);
	});

	return {
		active,
		portfolioConfig,
		toggleId,
		ariaLabel,
		buttonAriaLabel,
		getTranslations,
		openPortfolio,
		handleToggleChange,
		deactivateToggle,
	};
}
